import { DateTime } from 'luxon'

import { Nullable } from '@types'

import { getApiClient } from '@modules/chat/api/apiClient'
import { Company } from '@modules/search/types'
import apiClient from '@shared/apiClient'

import { CompanyContactsMap, Meeting, MeetingOverview, MeetingStatus, StreamMessageDto } from '../types'

export const fetchContactsOverview = async ({ companyIds }: { companyIds: Company['id'][] }): Promise<CompanyContactsMap> => {
  const [partneringResponse, inMailingResponse] = await Promise.all([
    apiClient.partneringOne('partnering').post('p1plus/companies-contacts', JSON.stringify({ companyIds })),
    getApiClient().post('/corporate-memory/counts', JSON.stringify({ companyIds })),
  ])

  const partneringCountsMap = partneringResponse.data?.data as CompanyContactsMap
  const inMailingCountsMap = inMailingResponse.data

  return mergeCounts(partneringCountsMap, inMailingCountsMap)
}

const mergeRecentMeetings = (
  partneringRecentMeeting: Nullable<Meeting>,
  inMailingRecentMeeting: Nullable<Meeting>,
): Nullable<Meeting> => {
  if (partneringRecentMeeting && inMailingRecentMeeting) {
    const partneringRecentMeetingDate = DateTime.fromISO(partneringRecentMeeting.date)
    const inMailingRecentMeetingDate = DateTime.fromISO(inMailingRecentMeeting.date)

    if (Number(partneringRecentMeetingDate) - Number(inMailingRecentMeetingDate) > 0) {
      return partneringRecentMeeting
    }

    return inMailingRecentMeeting
  }

  if (partneringRecentMeeting) {
    return partneringRecentMeeting
  }

  if (inMailingRecentMeeting) {
    return inMailingRecentMeeting
  }

  return null
}

const mergeCounts = (
  partneringCountsMap?: CompanyContactsMap,
  inMailingCountsMap?: Record<string, { count: number; recentMeeting: Nullable<Meeting> }>,
): CompanyContactsMap => {
  if (partneringCountsMap && inMailingCountsMap) {
    // Combine both
    const data = Object.entries(partneringCountsMap).reduce((acc, [companyId, countsOverview]) => {
      const inMailingCounts = inMailingCountsMap[companyId]?.count
      const inMailingRecentMeeting = inMailingCountsMap[companyId]?.recentMeeting

      if (inMailingCounts) {
        return {
          ...acc,
          [companyId]: {
            ...countsOverview,
            counts: {
              ...countsOverview.counts,
              [MeetingStatus.INMAIL]: inMailingCounts,
            },
            recentMeeting: mergeRecentMeetings(countsOverview.recentMeeting, inMailingRecentMeeting),
          },
        }
      }

      return {
        ...acc,
        [companyId]: countsOverview,
      }
    }, {})

    return Object.entries(inMailingCountsMap).reduce((acc, [companyId, inMailData]) => {
      if ((acc as CompanyContactsMap)[companyId]) {
        return acc
      }

      return {
        ...acc,
        [companyId]: { counts: { [MeetingStatus.INMAIL]: inMailData.count }, recentMeeting: inMailData.recentMeeting },
      }
    }, data)
  }

  if (partneringCountsMap) {
    return partneringCountsMap
  }

  if (inMailingCountsMap) {
    // Convert inMailingCountsMap format
    return Object.entries(inMailingCountsMap).reduce(
      (acc, [companyId, inMailData]) => ({
        ...acc,
        [companyId]: { counts: { [MeetingStatus.INMAIL]: inMailData.count }, recentMeeting: inMailData.recentMeeting },
      }),
      {},
    )
  }

  return {}
}

export interface StreamMessagesResponse {
  result: StreamMessageDto[]
  total: number
}

export const fetchStreamMessagesFn = async ({
  eventId,
  streamId,
  page = 0,
  pageSize,
}: {
  eventId: number
  streamId: string
  page: number
  pageSize: number
}): Promise<StreamMessagesResponse> => {
  const response = await apiClient
    .partneringOne('requesting')
    .get(`p1plus/${eventId}/streams/${streamId}/messages?from=${page * pageSize}&size=${pageSize}`)

  return response.data?.data
}

export const fetchYearsWithContactsFn = async ({ companyId }: { companyId: number }): Promise<number[]> => {
  const [partneringResponse, inMailingResponse] = await Promise.all([
    apiClient
      .partneringOne('partnering')
      .post('p1plus/companies-contacts/years', JSON.stringify({ companyId: Number(companyId) })),
    getApiClient().get(`/corporate-memory/${companyId}/years`),
  ])

  return [
    ...Array.from(
      new Set([
        ...(partneringResponse.data?.data?.length ? partneringResponse.data?.data ?? [] : []),
        ...(inMailingResponse.data ?? []),
      ]),
    ),
  ]
}

export const fetchContactsForAYearFn = async ({
  companyId,
  year,
}: {
  companyId: number
  year: number
}): Promise<MeetingOverview[]> => {
  const [partneringResponse, inMailingResponse] = await Promise.all([
    apiClient
      .partneringOne('partnering')
      .post('p1plus/companies-contacts/by-year', JSON.stringify({ companyId: Number(companyId), year })),
    getApiClient().get(`/corporate-memory/events/${companyId}/${year}`),
  ])

  return [
    ...(partneringResponse.data?.data?.length ? partneringResponse.data?.data ?? [] : []),
    ...(inMailingResponse.data ?? []),
  ]
}

export const fetchContactsForADayFn = async ({ companyId, date }: { companyId: number; date: string }): Promise<Meeting[]> => {
  const [partneringResponse, inMailingResponse] = await Promise.all([
    apiClient
      .partneringOne('partnering')
      .post('p1plus/companies-contacts/by-date', JSON.stringify({ companyId: Number(companyId), date })),
    getApiClient().get(`/corporate-memory/events/${companyId}`, { params: { date } }),
  ])

  return [
    ...(partneringResponse.data?.data?.length ? partneringResponse.data?.data ?? [] : []),
    ...(inMailingResponse.data ?? []),
  ]
}

export const fetchContactIds = async ({ type, hasAttended }: { type?: string; hasAttended?: boolean }): Promise<number[]> => {
  let queryString = ''

  if (type && hasAttended) {
    queryString = `?contactType=${type}&hasAttended=true`
  } else if (type) {
    queryString = `?contactType=${type}`
  } else if (hasAttended) {
    queryString = '?hasAttended=true'
  }

  const response = await getApiClient().get(`/corporate-memory/history-contact-company-ids${queryString}`)

  return response.data
}
